const deps = [];

/**
 * A service to take a series of event and status data and return a single score
 * for consumption by the Dashboard app. This is intended to to reduce cognitive
 * overhead for bookkeepers by sorting the client list by a priority score.
 */
class PriorityService {
  /**
   * Returns the mapped constant from the passed in importance level
   * @param {string} importance - 'high', 'medium' or 'low'
   */
  getImportanceConstant(importance) {
    const twoHoursInMillis = 3600 * 2 * 1000;
    const halfDayInMillis = 3600 * 12 * 1000;
    const threeDaysInMillis = 3 * 24 * 3600 * 1000;

    const importanceConstants = {
      high: twoHoursInMillis,
      medium: halfDayInMillis,
      low: threeDaysInMillis,
    };

    return importanceConstants[importance];
  }

  /**
   * Calculates the score for a single time, given some boundaries and a
   * constant representing the importance (urgency) of the given item
   * @param {number} time - The timestamp of the item to be scored
   * @param {Object} scoreBoundaries - The score boundaries of the item
   * @param {number} scoreBoundaries.base - The lowest amount of a non-zero score
   * @param {number} scoreBoundaries.variable - The amount a score may increase beyond the base
   * @param {number} importance - The urgency of the item to be acted on
   */
  calculateSingleScore(time, scoreBoundaries, importance) {
    if (time === null) {
      return 0;
    }

    const age = this.currentTime - time;
    const importanceConstant = this.getImportanceConstant(importance);
    const multiplier = age / (age + importanceConstant);
    const ageScore = multiplier * scoreBoundaries.variable;
    const score = scoreBoundaries.base + ageScore;

    return score;
  }

  /**
   * A map of times we consider for scoring, where each value represents the
   * oldest occurrence of an event or status that needs to be acted on. All
   * times in this map will be in milliseconds
   * @typedef TimesMap
   * @type {Object<string, ?Date>}
   *
   * Calculate the score for all events
   * @param {TimesMap} eventTimes
   */
  calculateEventScore({ message, transactionComment, categorizationComment, documentUpload }) {
    const scoreBoundaries = {
      message: { base: 50, variable: 30 },
      categorizationComment: { base: 4, variable: 4 },
      documentUpload: { base: 4, variable: 0 },
    };

    const messageScore = this.calculateSingleScore(message, scoreBoundaries.message, 'high');
    const transactionCommentScore = this.calculateSingleScore(
      transactionComment,
      scoreBoundaries.message,
      'high',
    );
    const categorizationCommentScore = this.calculateSingleScore(
      categorizationComment,
      scoreBoundaries.categorizationComment,
      'medium',
    );

    const documentUploadAge = documentUpload && this.currentTime - documentUpload;
    const fiveDaysInMillis = 5 * 24 * 3600 * 1000;
    const isDocumentUploadRecent = documentUpload && documentUploadAge < fiveDaysInMillis;
    const documentUploadScore = isDocumentUploadRecent ? 4 : 0;

    const scores = [
      Math.max(messageScore, transactionCommentScore),
      categorizationCommentScore,
      documentUploadScore,
    ];

    return scores.reduce((acc, score) => acc + score);
  }

  /**
   * Calculate the score for last contact
   * @param {TimesMap} times
   */
  calculateLastContactScore({ lastContact, revisionRequired }) {
    const hasRevisionRequired = revisionRequired !== null;
    const tenDaysInMillis = 10 * 24 * 3600 * 1000;
    const isLastContactTooOld = this.currentTime - lastContact > tenDaysInMillis;
    const lastContactScore = hasRevisionRequired && isLastContactTooOld ? 2 : 0;

    return lastContactScore;
  }

  /**
   * Calculate the score for all of the green statuses
   * @param {TimesMap} times
   */
  calculateGreenStatusScore(times) {
    const scoreBoundaries = {
      revisionRequired: { base: 0.5, variable: 1.5 },
      dataRequested: { base: 0.6, variable: 0.9 },
      readyReview: { base: 0.1, variable: 0.7 },
      readyAccount: { base: 0.1, variable: 0.6 },
      readyAddons: { base: 0.1, variable: 0.5 },
      readyCategorization: { base: 0.1, variable: 0.2 },
      readyUploads: { base: 0, variable: 0.1 },
    };

    const scores = Object.keys(scoreBoundaries).map(greenState =>
      this.calculateSingleScore(times[greenState], scoreBoundaries[greenState], 'low'),
    );

    return scores.reduce((acc, score) => acc + score);
  }

  /**
   * The public interface for extracting a score from a map of times to be scored
   * @param {TimesMap} times
   * @property {?Date} times.message - The oldest unaddressed message
   * @property {?Date} times.transactionComment - The oldest unaddressed transaction comment
   * @property {?Date} times.categorizationComment - The oldest unaddressed categorization comment
   * @property {?Date} times.documentUpload - The oldest unaddressed document upload
   * @property {?Date} times.lastContact - The last time the client was contacted
   * @property {?Date} times.revisionRequired - The oldest time of a 'Revision Required' status
   * @property {?Date} times.dataRequested - The oldest time of a 'Data Requested' status
   * @property {?Date} times.readyReview - The oldest time of a 'Ready' status for a review
   * @property {?Date} times.readyAccount - The oldest time of a 'Ready' status for an account
   * @property {?Date} times.readyAddons - The oldest time of a 'Ready' status for an add-on
   * @property {?Date} times.readyCategorization - The oldest time of a 'Ready'
   * status for a categorization
   * @property {?Date} times.readyUploads - The oldest time of a 'Ready' status for process uploads
   * @param {Date} currentTime - the current time as passed down from the UI. We accept a current
   * time from higher up so that it is the same between different clients.
   */
  getScore(times, currentTime) {
    this.currentTime = currentTime;
    const eventScore = this.calculateEventScore(times);
    const lastContactScore = this.calculateLastContactScore(times);
    const greenStatusScore = this.calculateGreenStatusScore(times);

    return eventScore + lastContactScore + greenStatusScore;
  }
}

module.exports = angular
  .module('bench.data.service.priority', deps)
  .service('PriorityService', PriorityService);
